import React from 'react';
import styled from 'styled-components';
import { X } from 'lucide-react';

export interface MultiSelectOption {
  id: number;
  name: string;
}

interface MultiSelectChipsProps {
  options: MultiSelectOption[];      // כל האפשרויות הזמינות לבחירה
  selectedIds: number[];
  onChange: (ids: number[]) => void;
  disabled?: boolean;
  placeholder?: string;              // טקסט ה-"הוסף" בבורר
  emptyText?: string;                // טקסט כשאין בחירה
}

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const Chips = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.4rem;
`;

const Chip = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  background: #e6f0ff;
  border: 1px solid #c2dbff;
  color: var(--text-primary);
  border-radius: 999px;
  padding: 0.2rem 0.6rem;
  font-size: 0.85rem;
`;

const RemoveButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: transparent;
  border: none;
  cursor: pointer;
  color: var(--text-secondary);
  padding: 0;

  &:hover { color: var(--error-color); }
  &:disabled { opacity: 0.5; cursor: not-allowed; }
`;

const EmptyText = styled.span`
  color: var(--text-secondary);
  font-size: 0.85rem;
  font-style: italic;
`;

const AddSelect = styled.select`
  padding: 0.6rem 0.75rem;
  border: 1px solid var(--border-color);
  border-radius: 4px;
  font-size: 0.95rem;
  direction: rtl;
  text-align: right;
  background-color: white;

  &:focus {
    outline: none;
    border-color: var(--primary-color);
  }
  &:disabled { opacity: 0.6; cursor: not-allowed; }
`;

/**
 * בורר-רבים מבוסס chips: מציג את הנבחרים כתגיות הניתנות להסרה, ובורר להוספת אפשרות שטרם נבחרה.
 * גנרי לפי id/name — משמש לשיוך מחלקות ולפילטר מחלקות (כולל "ללא מחלקה" עם id=-1).
 */
const MultiSelectChips: React.FC<MultiSelectChipsProps> = ({
  options,
  selectedIds,
  onChange,
  disabled = false,
  placeholder = '+ הוסף',
  emptyText = 'לא נבחר',
}) => {
  const selectedSet = new Set(selectedIds);
  const selectedOptions = options.filter(o => selectedSet.has(o.id));
  const available = options.filter(o => !selectedSet.has(o.id));

  const add = (id: number) => {
    if (!selectedSet.has(id)) onChange([...selectedIds, id]);
  };
  const remove = (id: number) => onChange(selectedIds.filter(x => x !== id));

  return (
    <Wrapper>
      <Chips>
        {selectedOptions.length === 0 && <EmptyText>{emptyText}</EmptyText>}
        {selectedOptions.map(o => (
          <Chip key={o.id}>
            {o.name}
            <RemoveButton type="button" onClick={() => remove(o.id)} disabled={disabled} aria-label={`הסר ${o.name}`}>
              <X size={13} />
            </RemoveButton>
          </Chip>
        ))}
      </Chips>
      {available.length > 0 && (
        <AddSelect
          value=""
          disabled={disabled}
          onChange={(e) => {
            if (e.target.value !== '') add(parseInt(e.target.value));
          }}
        >
          <option value="">{placeholder}</option>
          {available.map(o => (
            <option key={o.id} value={o.id}>{o.name}</option>
          ))}
        </AddSelect>
      )}
    </Wrapper>
  );
};

export default MultiSelectChips;
